import { Attr, IndexedMesh } from "./indexed-mesh";
import { IndexedMeshWriter } from "./indexed-mesh-writer";


type GridLayoutAttrs = {
    normal: Attr,
    uv: Attr,
}

const ascending = (a: number, b: number) => a - b;
const byIndexAscending = (a: Vert, b: Vert) => a.i - b.i;

class Vert {
    x: number;
    y: number;
    i: number | null;
    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
        this.i = null;
    }
}

export class GridLayout extends IndexedMeshWriter<GridLayoutAttrs> {

    rects: number[] = [];
    voids: number[] = [];

    /**
     * Contains all of the _potential_ vertices at every (x, y) pairing for all
     * rects and voids. Some of these may not be included in the output since
     * some may fall inside voids.
     *
     * This is a temporary used to store data between calls to
     * computeAttributeCounts and computeMeshData.
     */
    verts: Vert[][];

    /**
     * This is a temporary used to store data between calls to
     * computeAttributeCounts and computeMeshData.
     */
    planes: Vert[];

    /**
     * Fills an area. The output will have a surface covering the area of the
     * given rectangle, minus any voids.
     */
    rect(x: number, y: number, width: number, height: number) {
        this.rects.push(x, y, x+width, y+height);
    }

    /**
     * Used internally to check if a rectangle is inside or falls on the border
     * of a void. This is used internally to check pairings of adjacent grid
     * lines for whether they should generate a visible face or not.
     */
    isInRect(x1: number, y1: number, x2: number, y2: number) {
        const r = this.rects;
        for (let i=0; i < r.length; i += 4) {
            if (x1 >= r[i] && y1 >= r[i+1] && x2 <= r[i+2] && y2 <= r[i+3]) {
                return true;
            }
        }
        return false;
    }

    /**
     * Adds a void that will not receive coverage in the output surface. Voids
     * can overlap.
     */
    void(x: number, y: number, width: number, height: number) {
        this.voids.push(x, y, x+width, y+height);
    }

    /**
     * Used internally to check if a rectangle is inside or falls on the border
     * of a void. This is used internally to check pairings of adjacent grid
     * lines for whether they should generate a visible face or not.
     */
    isInVoid(x1: number, y1: number, x2: number, y2: number) {
        const v = this.voids;
        for (let i=0; i < v.length; i += 4) {
            if (x1 >= v[i] && y1 >= v[i+1] && x2 <= v[i+2] && y2 <= v[i+3]) {
                return true;
            }
        }
        return false;
    }

    clone() {
        const clone = new GridLayout();
        clone.rects = Array.from(this.rects);
        clone.voids = Array.from(this.voids);
        return clone;
    }

    computeAttributeCounts(): IndexedMesh<GridLayoutAttrs> {
        // In this implementation we're computing most of the output data in
        // order to provide output counts. This data includes the vertices and
        // indexes in the `verts` member, and the planes in the `planes`
        // member. The computeMeshData method will be considerable simpler.

        const mesh = new IndexedMesh({
            normal: new Attr(3),
            uv: new Attr(2),
        });

        // Find all the unique x and y values
        const
            xset = new Set<number>(),
            yset = new Set<number>();
        {
            let i = 0;
            while(i < this.rects.length) {
                xset.add(this.rects[i++]);
                yset.add(this.rects[i++]);
            }
        }
        {
            let i = 0;
            while(i < this.voids.length) {
                xset.add(this.voids[i++]);
                yset.add(this.voids[i++]);
            }
        }

        const
            xs = Array.from(xset),
            ys = Array.from(yset);

        xs.sort(ascending);
        ys.sort(ascending);

        // Put all XY pairs into the verts array
        const verts: Vert[][] = this.verts = new Array(xs.length);
        {
            let i = 0;
            for(const x of xs) {
                verts[i] = new Array(ys.length);
                let j = 0;
                for(const y of ys) {
                    verts[i][j++] = new Vert(x, y);
                }
                i++;
            }
        }

        // We're going to iterate over the xs and ys again, this time checking
        // if they do or do not produce a plane in the output. If they do, then
        // we both push them onto the planes array, and we mark them as used
        // by assigning a unique index to the vert's `i` property.
        const planes: Vert[] = this.planes = [];
        let i = 0; // counts vertecies in use
        for(let j = 1; j < xs.length; j++) {
            const x1 = xs[j-1], x2 = xs[j];

            for(let k = 1; k < ys.length; k++) {
                const y1 = ys[k-1], y2 = ys[k];

                if(this.isInVoid(x1, y1, x2, y2)) {
                    // nothing to do, skip this cell
                } else if(this.isInRect(x1, y1, x2, y2)) {
                    const
                        a = verts[j-1][k-1],
                        b = verts[j  ][k-1],
                        c = verts[j  ][k  ],
                        d = verts[j-1][k  ];
                    a.i = a.i == null ? i++ : a.i;
                    b.i = b.i == null ? i++ : b.i;
                    c.i = c.i == null ? i++ : c.i;
                    d.i = d.i == null ? i++ : d.i;
                    planes.push(a, b, c, d);
                }
            }
        }

        const
            // i was used to count how many verticies were in use in the loop
            // just above
            vertexes = i,
            // the planes array contains four vertices per quad in the output,
            // and each quad consists of two triangles. The arithmetic
            // simplifies so that we have N / 2 triangles for N entries in the
            // planes array.
            triangles = planes.length / 2;

        mesh.index.count = triangles * 3;
        mesh.position.count = vertexes;
        mesh.attrs.normal.count = vertexes;
        mesh.attrs.uv.count = vertexes;

        return mesh;
    }

    computeMeshData(mesh: IndexedMesh<GridLayoutAttrs>): void {
        const verts = this.verts.flat().filter(v => v.i != null);
        verts.sort(byIndexAscending);
        let p = 0, u = 0;
        for(const vert of verts) {
            mesh.position.data[p++] = vert.x;
            mesh.position.data[p++] = vert.y;
            mesh.position.data[p++] = 0;
            mesh.attrs.uv.data[u++] = vert.x;
            mesh.attrs.uv.data[u++] = vert.y;
        }

        const norm = mesh.attrs.normal.data;
        let n = 0;
        while( n < norm.length) {
            norm[n++] = 0; norm[n++] = 0; norm[n++] = 1;
        }

        const
            planes = this.planes,
            idx = mesh.index.data;
        let i = 0;
        for(let j = 0; j < planes.length; j += 4) {
            idx[i++] = planes[j  ].i;
            idx[i++] = planes[j+1].i;
            idx[i++] = planes[j+2].i;

            idx[i++] = planes[j  ].i;
            idx[i++] = planes[j+2].i;
            idx[i++] = planes[j+3].i;
        }
    }
}
